package com.karyadina.ui

import androidx.compose.runtime.Composable
import androidx.compose.runtime.CompositionLocalProvider
import androidx.compose.runtime.staticCompositionLocalOf
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.remember
import com.karyadina.service.KeyValueStorage
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.launch

// 1. The Dynamic Strings Interface
data class AppStrings(
    val appTitle: String = "Quick Notes",
    val addNote: String = "Add Note",
    val extractText: String = "Extract Text",
    val assistant: String = "Assistant",
    val notes: String = "Notes",
    val tasks: String = "Tasks",
    val searchHint: String = "Search notes..."
)



val EnglishStrings = AppStrings()

// 2. The Locale Manager
class AppLocaleManager(
    private val storage: KeyValueStorage,
    private val translationService: com.karyadina.service.TranslationService
) {
    private val _strings = MutableStateFlow(EnglishStrings)
    val strings = _strings.asStateFlow()
    
    // In-memory cache of languages we have already generated
    private val cache = mutableMapOf<String, AppStrings>("en" to EnglishStrings)
    
    private val scope = kotlinx.coroutines.CoroutineScope(kotlinx.coroutines.Dispatchers.Main)

    init {
        // Restore saved language on startup
        val savedCode = storage.loadString("app_language")
        if (savedCode != null && savedCode != "en") {
            scope.launch {
                switchLanguage(savedCode)
            }
        }
    }

    suspend fun switchLanguage(code: String) {
        if (code == "en") {
            _strings.value = EnglishStrings
            return
        }
        
        // 1. Check cache
        if (cache.containsKey(code)) {
            _strings.value = cache[code]!!
            return
        }
        
        // 2. Generate on the fly (The "AI" part)
        // We take the English source and translate it.
        val base = EnglishStrings
        
        // Note: In a real production app, we would batch this or use reflection/serialization.
        // For now, mapping manually to prove the concept.
        val newStrings = AppStrings(
            appTitle = translate(base.appTitle, code),
            addNote = translate(base.addNote, code),
            extractText = translate(base.extractText, code),
            assistant = translate(base.assistant, code),
            notes = translate(base.notes, code),
            tasks = translate(base.tasks, code),
            searchHint = translate(base.searchHint, code)
        )
        
        cache[code] = newStrings
        _strings.value = newStrings
    }
    
    private suspend fun translate(text: String, targetLang: String): String {
        try {
            // Using our TranslationService (ML Kit or LLM)
            val result = translationService.translate(text, "en", targetLang)
            return result.getOrDefault(text)
        } catch (e: Exception) {
            return text
        }
    }
}

// 3. Composition Local for Global Access
val LocalAppStrings = staticCompositionLocalOf { EnglishStrings }
